import * as React from 'react'
import { cn } from '@/lib/utils'
import { normalizeMentionDisplayName } from '@/shared/utils/mentions'
import { Avatar } from './Avatar'
import { Input } from './Input'

export type MentionUser = {
  id: string
  full_name?: string | null
  avatar_url?: string | null
}

export type MentionAutocompleteProps = {
  items: MentionUser[]
  activeIndex: number
  loading?: boolean
  hasMore?: boolean
  loadingMore?: boolean
  emptyLabel?: string
  query?: string
  searchPlaceholder?: string
  onQueryChange?: (query: string) => void
  onRequestClose?: () => void
  onMoveActiveIndex?: (delta: number) => void
  onSelectActive?: () => void
  onHoverIndex?: (index: number) => void
  onLoadMore?: () => void
  onSelect: (user: MentionUser) => void
  className?: string
}

export function MentionAutocomplete({
  items,
  activeIndex,
  loading,
  hasMore,
  loadingMore,
  emptyLabel = 'Nenhum usuário encontrado',
  query,
  searchPlaceholder = 'Buscar usuário...',
  onQueryChange,
  onRequestClose,
  onMoveActiveIndex,
  onSelectActive,
  onHoverIndex,
  onLoadMore,
  onSelect,
  className,
}: MentionAutocompleteProps) {
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const showSearch = !!onQueryChange

  React.useEffect(() => {
    const el = listRef.current
    if (!el) return
    const selected = el.querySelector('[aria-selected="true"]')
    if (!selected || !(selected instanceof HTMLElement)) return
    try {
      selected.scrollIntoView({ block: 'nearest' })
    } catch {
      // ignore
    }
  }, [activeIndex, items.length])

  const handleSearchKeyDown = React.useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      const key = ev.key || ''

      if (key === 'Escape') {
        ev.preventDefault()
        ev.stopPropagation()
        onRequestClose?.()
        return
      }

      if (key === 'ArrowDown') {
        ev.preventDefault()
        ev.stopPropagation()
        onMoveActiveIndex?.(1)
        return
      }

      if (key === 'ArrowUp') {
        ev.preventDefault()
        ev.stopPropagation()
        onMoveActiveIndex?.(-1)
        return
      }

      if (key === 'Enter') {
        ev.preventDefault()
        ev.stopPropagation()
        onSelectActive?.()
      }
    },
    [onMoveActiveIndex, onRequestClose, onSelectActive],
  )

  const handleListScroll = React.useCallback(() => {
    if (!hasMore) return
    if (loading || loadingMore) return
    if (!onLoadMore) return

    const el = listRef.current
    if (!el) return

    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
    if (remaining <= 24) {
      onLoadMore()
    }
  }, [hasMore, loading, loadingMore, onLoadMore])

  return (
    <div
      className={cn(
        'w-[360px] max-w-[calc(100vw-16px)] overflow-hidden rounded-xl border border-[#2a2a2a] bg-[#151515] shadow-xl',
        className,
      )}
      role="listbox"
      aria-label="Sugestões de menção"
    >
      {showSearch && (
        <div className="border-b border-[#2a2a2a] px-3 py-2">
          <Input
            value={query ?? ''}
            onChange={(e) => onQueryChange?.(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn(
              'h-9 border-[#2a2a2a] bg-[#0f0f0f] px-3 py-1 text-sm text-foreground',
              'focus:ring-2 focus:ring-sky-500/40',
            )}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
      )}

      <div
        ref={listRef}
        className={cn(
          'overflow-auto',
          showSearch ? 'max-h-[268px]' : 'max-h-[320px]',
        )}
        onScroll={handleListScroll}
      >
        {loading ? (
          <div className="px-3 py-2 text-sm text-foreground/70">
            Carregando...
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-2 text-sm text-foreground/70">
            {emptyLabel}
          </div>
        ) : (
          <>
            {items.map((u, idx) => {
              const label = normalizeMentionDisplayName(u.full_name)
              const selected = idx === activeIndex
              return (
                <button
                  key={u.id}
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-foreground/90 hover:bg-[#1f1f1f]',
                    selected && 'bg-[#1f1f1f]',
                  )}
                  role="option"
                  aria-selected={selected ? 'true' : 'false'}
                  onMouseDown={(e) => {
                    e.preventDefault()
                  }}
                  onMouseEnter={() => onHoverIndex?.(idx)}
                  onClick={() => onSelect(u)}
                >
                  <Avatar
                    className="h-8 w-8"
                    src={u.avatar_url || undefined}
                    alt={label}
                    fallback={label?.[0]?.toUpperCase() || '?'}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {label}
                    </div>
                  </div>
                </button>
              )
            })}

            {loadingMore && (
              <div className="px-3 py-2 text-sm text-foreground/70">
                Carregando...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
